import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { MessageCircle, LogOut, Clock, Sparkles } from 'lucide-react';

/**
 * TrialEnded — shown when /billing/status returns status="expired", or
 * after any core API call 402s with `trial_expired`. Payment is manual
 * for now: parents tap the WhatsApp button to reach support, we confirm
 * UPI separately, then the admin panel extends `access_expires_at`.
 */
export default function TrialEnded() {
  const nav = useNavigate();
  const { user, billing, refreshBilling, logout } = useApp();
  const [plans, setPlans] = useState([]);

  // Pull optional plans copy so we can show real pricing if the admin
  // has set it, else fall back to the generic "contact us" prompt.
  useEffect(() => {
    api.get('/config/plans').then(({ data }) => setPlans(data?.plans || [])).catch(() => {});
    refreshBilling();
  }, [refreshBilling]);

  // If the parent got here but /billing/status now says they're active
  // again (admin extended while this tab was open), bounce them home.
  useEffect(() => {
    if (billing && billing.status !== 'expired') nav('/profiles', { replace: true });
  }, [billing, nav]);

  const waUrl = billing?.whatsapp_url_continue
    || `https://wa.me/919599668449?text=${encodeURIComponent(`Hi, I would like to continue using ReadBuddy. My name is ${user?.name || ''} and my email is ${user?.email || ''}`)}`;

  const handleLogout = async () => { await logout(); nav('/', { replace: true }); };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center px-6 py-10 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#F4B740] blur-3xl opacity-70"/>
      <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#D97398] blur-3xl opacity-70"/>

      <div data-testid="trial-ended" className="relative z-10 w-full max-w-md bg-white/95 backdrop-blur rounded-3xl p-7 shadow-xl text-center">
        <div className="w-16 h-16 rounded-full bg-[#FFEDD5] mx-auto mb-4 flex items-center justify-center">
          <Sparkles size={30} className="text-[#FF6B1A]" strokeWidth={2.5}/>
        </div>
        <h1 className="text-2xl font-extrabold text-[#2B2140] mb-2">Your free trial has ended</h1>
        <p className="text-sm text-slate-600 leading-relaxed">
          Continue enjoying ReadBuddy by upgrading your access.
        </p>

        {plans.length > 0 && (
          <div data-testid="trial-plans" className="mt-5 space-y-3 text-left">
            {plans.map((p, i) => (
              <div key={i} className="border-2 border-slate-200 rounded-2xl p-4">
                <div className="flex items-center justify-between">
                  <div className="font-extrabold text-[#2B2140]">{p.name || 'Plan'}</div>
                  {p.price && <div className="text-[#FF6B1A] font-bold">{p.price}</div>}
                </div>
                {p.description && <div className="text-sm text-slate-600 mt-1">{p.description}</div>}
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 bg-[#FFF7ED] border border-[#FED7AA] rounded-2xl p-4 flex items-start gap-3 text-left">
          <Clock size={18} className="text-[#FF6B1A] shrink-0 mt-0.5"/>
          <div className="text-sm text-slate-700">
            Our support team is available from <span className="font-bold">{billing?.support_hours || '10:00 AM to 9:00 PM'}</span> to assist you.
          </div>
        </div>

        <a data-testid="trial-whatsapp-btn" href={waUrl} target="_blank" rel="noopener noreferrer"
          className="pushable mt-5 w-full bg-[#25D366] hover:bg-[#20BF5B] text-white font-bold py-3 rounded-full inline-flex items-center justify-center gap-2 text-base">
          <MessageCircle size={18} strokeWidth={2.5}/>
          Contact us on WhatsApp
        </a>

        <button data-testid="trial-logout-btn" onClick={handleLogout}
          className="mt-3 w-full bg-white border-2 border-slate-200 text-slate-700 font-bold py-3 rounded-full inline-flex items-center justify-center gap-2 text-sm hover:border-slate-400">
          <LogOut size={16} strokeWidth={2.5}/>
          Log out
        </button>
      </div>
    </div>
  );
}
